import { Check, X, Eye } from 'lucide-react-native';
import i18next from 'i18next';
import type { ActionHandlerProps, ActionConfig } from '../DataTable/actionTypes';
import log from '../../../common/utils/logger';
import { ModuleLecturerResponse } from '../../../common/api/models';

interface HookFunctions {
  approveModuleVersion: (moduleId: string, version: string, comment?: string) => Promise<void>;
  rejectModuleVersion: (moduleId: string, version: string, comment?: string) => Promise<void>;
}

/**
 * Creates a module approval action handler
 * @param {string} action - Either 'approve' or 'reject'
 * @param {Object} hookFunctions - Functions from useAdminModuleApproval hook
 * @returns {Function} Action handler function
 */
const createModuleApprovalAction = (action: 'approve' | 'reject', hookFunctions: HookFunctions) => async ({ data, utils }: ActionHandlerProps) => {
  const {
    showDialog, hideDialog, showToastMessage, setRefreshKey,
  } = utils;
  const item = data as ModuleLecturerResponse & { version: string };

  // Use the passed hook functions instead of calling the hook directly
  const { approveModuleVersion, rejectModuleVersion } = hookFunctions;

  // Determine which action function to use
  const actionFn = action === 'approve' ? approveModuleVersion : rejectModuleVersion;

  // Show confirmation dialog based on action type
  const pendingMeta = item.stateTransitions?.[item.stateTransitions.length - 1]?.metadata;
  const offerHeadline = pendingMeta?.headline as string | undefined;
  const offerImpact = pendingMeta?.impactNotice as string | undefined;
  const approvalTypeLabel = pendingMeta?.approvalType === 'offer_upgrade' ? '\n\nApproval type: Offer upgrade to students.' : '';
  const isReplaceVersionApproval = action === 'approve' && pendingMeta?.approvalType === 'replace_version';
  const loadingMessage = isReplaceVersionApproval
    ? i18next.t('admin.moduleApprovals.loading.replaceVersion')
    : i18next.t(`admin.moduleApprovals.loading.${action}`);
  const offerDetailLines: string[] = [];
  if (offerHeadline?.trim()) {
    offerDetailLines.push(`Offer headline: ${offerHeadline.trim()}`);
  }
  if (offerImpact?.trim()) {
    offerDetailLines.push(`Impact: ${offerImpact.trim()}`);
  }
  const offerDetails = offerDetailLines.length > 0
    ? `\n\n${offerDetailLines.join('\n')}`
    : '';

  showDialog({
    title: `${action === 'approve' ? 'Approve' : 'Reject'} Module Version`,
    message: `Are you sure you want to ${action === 'approve' ? 'approve' : 'reject'} this module version?${
      action === 'reject' ? '\n\nThis will return the module to draft state for the author to revise.' : ''
    }${approvalTypeLabel}${offerDetails}`,
    buttons: [
      { text: 'Cancel', variant: 'outline', action: 'secondary' },
      {
        text: action === 'approve' ? 'Approve' : 'Reject',
        action: action === 'approve' ? 'positive' : 'negative',
        preventAutoClose: true,
        onPress: async () => {
          showDialog({
            title: `${action === 'approve' ? 'Approve' : 'Reject'} Module Version`,
            message: loadingMessage,
            isPersistent: true,
            buttons: [
              {
                text: action === 'approve' ? 'Approve' : 'Reject',
                action: action === 'approve' ? 'positive' : 'negative',
                isLoading: true,
                isDisabled: true,
              },
            ],
          });
          try {
            utils.setActionRunning(true);
            // Comment field could be added in the future for providing feedback
            const comment = action === 'reject'
              ? 'Module version rejected. Please revise according to guidelines.'
              : 'Module version approved and published.';

            await actionFn(item.id, item.version, comment);

            showToastMessage(
              'success',
              `Module version ${action === 'approve' ? 'approved' : 'rejected'} successfully`,
            );

            // Refresh the data table
            setRefreshKey(Date.now());
            hideDialog();
          } catch (error: unknown) {
            hideDialog();
            const typedError = error as { response?: { data?: { message?: string } }; message?: string };
            const errorMessage = typedError.response?.data?.message
              || `Failed to ${action} module version: ${typedError.message}`;

            showToastMessage('error', errorMessage);
            log.error(`Error ${action}ing module version:`, error);
          } finally {
            utils.setActionRunning(false);
          }
        },
      },
    ],
  });
};

/**
 * Preview module action handler - navigates to the form navigator with preview action
 */
const createModulePreviewAction = () => async ({ data, utils }: ActionHandlerProps) => {
  const { navigation } = utils;
  const item = data as ModuleLecturerResponse & {
    version: string;
    sourceVersion?: string;
  };
  // Navigate to the CreateEditForm with preview action
  navigation.navigate('CreateEditForm', {
    objectName: 'modules/approvals',
    action: 'preview',
    id: item.id,
    data: {
      id: item.id,
      version: item.version,
      sourceVersion: item.sourceVersion,
      changelog: item.stateTransitions[item.stateTransitions.length - 1]?.metadata?.changelog,
      title: item.title,
    },
  });
};

/**
 * Creates module approval actions with the provided hook functions
 * @param {Object} hookFunctions - Functions from useAdminModuleApproval hook
 * @returns {Object} Module approval actions configuration
 */
const createModuleApprovalActions = (hookFunctions: HookFunctions): Record<string, Partial<ActionConfig>> => ({
  approve: {
    id: 'approve',
    label: 'Approve',
    icon: Check,
    position: 'row',
    handler: createModuleApprovalAction('approve', hookFunctions),
  },
  reject: {
    id: 'reject',
    label: 'Reject',
    icon: X,
    position: 'row',
    handler: createModuleApprovalAction('reject', hookFunctions),
  },
  preview: {
    id: 'preview',
    label: 'Preview',
    icon: Eye,
    position: 'row',
    handler: createModulePreviewAction(),
  },
});

export { createModuleApprovalActions };
// Export a dummy version for backwards compatibility
export const moduleApprovalActions = {};
